/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import * as moment from "moment";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DynamicFormComponent } from "../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../shared/components/dynamic-form/question-base";
import { DropdownOption } from "../../../shared/components/dynamic-form/question-dropdown";
import { TextboxQuestion } from "../../../shared/components/dynamic-form/question-textbox";
import { GenericModalComponent } from "../../../shared/components/generic-modal/generic-modal.component";
import { GenericModalData } from "../../../shared/components/generic-modal/generic-modal.type";
import { NotificationService } from "../../../shared/components/notification/notification.service";
import { MODAL } from "../../../shared/constants/components";
import { pattern } from "../../../shared/constants/patterns";
import { UserInfo } from "../../../shared/types/user-info";
import { Company } from "../companies/company.type";
import { ContractService } from "../contracts/contract.service";
import { Contract } from "../contracts/contract.type";
import { MaintenanceProvider } from "../maintenance-provider/maintenance-provider.types";
import { PartnerSpecialTypes } from "../partners/partner.type";
import { AssetService } from "./asset.service";
import { Asset, AssetOemModelType, AssetOemModelTypeRequest, Attribute, ContractAsset } from "./asset.type";
import { isAsset, validateDates } from "./assets.shared";
import { CreateAssetService } from "./create-asset/create-asset.service";

@Injectable({
  providedIn: "root",
})
export class AssetServiceUI {
  dialogRef: MatDialogRef<GenericModalComponent>;

  constructor(private dialog: MatDialog, private notificationService: NotificationService) {}

  showAssetCreationModal(
    company: Company,
    contract: Contract,
    currentUser: UserInfo,
    table: DataTableComponent<Asset>,
    createAssetService: CreateAssetService,
    contractService: ContractService,
    assetService: AssetService,
  ): MatDialogRef<GenericModalComponent<any>, any> {
    const questions: QuestionBase<string | boolean>[] = [];

    const isRelutech = company.partner.specialType === PartnerSpecialTypes.Relutech;

    createAssetService
      .generateQuestions({
        currentUser,
        partnerSkip: true,
        companyEdit: false,
        companyValue: company.id,
        companyOptions: [{ value: company.id, label: company.name }],
        isChannelWorksValue: false,
        contract,
        companyHidden: !!contract,
        hideContractNumber: true,
        hideQuotedSla: !isRelutech || currentUser.isCompany(),
        hideQuotedPrice: !isRelutech || currentUser.isCompany(),
        hideQuotedTerm: !isRelutech || currentUser.isCompany(),
        quotedSlaEdit: !currentUser.isCompany(),
        quotedPriceEdit: !currentUser.isCompany(),
        quotedTermEdit: !currentUser.isCompany(),
        startDateHidden: true,
        endDateHidden: true,
        coverageStatusHidden: true,
        slaHidden: true,
        dmrHidden: true,
        monitoringHidden: true,
        channelWorksCostHidden: true,
        partnerPriceHidden: true,
        clientPriceHidden: true,
        internalProviderHidden: true,
        contractHidden: false,
        startDateEdit: false,
        showFmvValue: company?.enableAssetCustomFields && currentUser.isGlobalAdmin(),
        showBookValue: company?.enableAssetCustomFields && currentUser.isGlobalAdmin(),
        bookValueEdit: company?.enableAssetCustomFields && currentUser.isGlobalAdmin(),
        fmvValueEdit: company?.enableAssetCustomFields && currentUser.isGlobalAdmin(),
      })
      .subscribe((qs) => Object.assign(questions, qs));

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: MODAL.WIDTH,
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: "Add Asset",
        component: {
          clazz: DynamicFormComponent,
          // output: "formSubmit",
          onSubmit: "onSubmit",
          inputs: {
            dialog: true,
            questions: questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "hasChanged",
              handler: async (dynamicForm: DynamicFormComponent, event: any) => {
                validateDates(event, dynamicForm);

                if (event.source?.id === "contractId") {
                  const contractId = event.value;
                  const termsKeys = [
                    "startDate",
                    "endDate",
                    "channelWorksCost",
                    "partnerPrice",
                    "clientPrice",
                    "slaId",
                    "dmr",
                    "monitoring",
                    "internalProviderId",
                  ];

                  if (contractId) {
                    const selectedContract = await this.getContract(contractService, contractId);

                    const { isPartnerPriceVisible, isPartnerPriceEnabled, isClientPriceVisible, isClientPriceEnabled } =
                      createAssetService;

                    // ChannelWorks price
                    if (currentUser.isGlobal()) {
                      dynamicForm.getQuestion("channelWorksCost").hide = false;
                      dynamicForm.form.controls["channelWorksCost"].setValue(undefined);
                      dynamicForm.form.controls["channelWorksCost"].enable();
                    }

                    // partner price
                    // handle visibility
                    if (isPartnerPriceVisible(currentUser, company, selectedContract)) {
                      dynamicForm.getQuestion("partnerPrice").hide = false;
                    } else {
                      dynamicForm.getQuestion("partnerPrice").hide = true;
                      dynamicForm.form.controls["partnerPrice"].setValue(undefined);
                    }
                    // handle enabled/disabled
                    if (isPartnerPriceEnabled(currentUser, company, selectedContract)) {
                      dynamicForm.form.controls["partnerPrice"].enable();
                    } else {
                      dynamicForm.form.controls["partnerPrice"].disable();
                    }
                    // client price
                    // handle visibility
                    if (isClientPriceVisible(currentUser, company, selectedContract)) {
                      dynamicForm.getQuestion("clientPrice").hide = false;
                    } else {
                      dynamicForm.getQuestion("clientPrice").hide = true;
                      dynamicForm.form.controls["clientPrice"].setValue(undefined);
                    }
                    // handle enabled/disabled
                    if (isClientPriceEnabled(currentUser, company, selectedContract)) {
                      dynamicForm.form.controls["clientPrice"].enable();
                    } else {
                      dynamicForm.form.controls["clientPrice"].disable();
                    }

                    // Prices are excluded here due they have a different behavior
                    const keysToExclude = ["channelWorksCost", "partnerPrice", "clientPrice"];

                    termsKeys
                      .filter((termKey) => !keysToExclude.includes(termKey))
                      .forEach((termKey) => {
                        if (termKey === "internalProviderId") {
                          if (currentUser.isGlobal()) {
                            dynamicForm.getQuestion(termKey).hide = false;
                            dynamicForm.form.controls[termKey].enable();
                            dynamicForm.form.controls[termKey].setValue(undefined);
                          }
                        } else if (termKey === "startDate" || termKey === "endDate") {
                          dynamicForm.getQuestion(termKey).minDate = moment(selectedContract.startDate).toDate();
                          dynamicForm.getQuestion(termKey).maxDate = moment(selectedContract.endDate).toDate();
                          dynamicForm.getQuestion(termKey).hide = false;
                          dynamicForm.form.controls[termKey].enable();
                          dynamicForm.form.controls[termKey].setValue(undefined);
                        } else {
                          dynamicForm.getQuestion(termKey).hide = false;
                          dynamicForm.form.controls[termKey].enable();
                          dynamicForm.form.controls[termKey].setValue(undefined);
                        }
                      });
                  } else {
                    termsKeys.forEach((termKey) => {
                      dynamicForm.getQuestion(termKey).hide = true;
                      dynamicForm.form.controls[termKey].setValue(undefined);
                      dynamicForm.form.controls[termKey].disable();
                      dynamicForm.form.controls[termKey].markAsPristine();
                    });
                  }
                }
              },
            },
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: Asset) => {
                this.addAsset(values, table, assetService);
              },
            },
          ],
        },
      },
    });

    return this.dialogRef;
  }

  /** Show a modal to create a new set of fields (OEM, Model # and Type) */
  showEquipmentIdentifiersCreationModal(
    assetService: AssetService,
    oem?: DropdownOption,
    model?: DropdownOption,
  ): MatDialogRef<GenericModalComponent, AssetOemModelType> {
    //#region Questions
    const questions: QuestionBase[] = [
      new TextboxQuestion({
        value: oem?.label,
        key: "oemName",
        label: "OEM",
        required: true,
        disabled: !!oem,
        order: 1,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "OEM is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "OEM is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "OEM is <strong>too long</strong>",
          },
        ],
      }),
      new TextboxQuestion({
        value: model?.label,
        key: "modelName",
        label: "Model #",
        required: true,
        disabled: !!model,
        order: 2,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Model # is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Model # is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(100),
            message: "Model # is <strong>too long</strong>",
          },
        ],
      }),
      new TextboxQuestion({
        key: "typeName",
        label: "Type",
        required: true,
        order: 3,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Type is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(2),
            message: "Type is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(100),
            message: "Type is <strong>too long</strong>",
          },
        ],
      }),
    ];
    //#endregion

    //#region Modal
    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: MODAL.WIDTH,
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: "Create New",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (
                _dynamicForm: DynamicFormComponent,
                values: { oemName: string; modelName: string; typeName: string },
              ) => {
                this.addAssetEquipment(
                  values,
                  { oemExternalId: +oem?.value, modelExternalId: +model?.value },
                  assetService,
                  _dynamicForm,
                );
              },
            },
          ],
        },
      },
    });
    //#endregion
    return this.dialogRef;
  }

  showTicketCreationWarningModal(assetOrContractAsset: Asset | ContractAsset): MatDialogRef<GenericModalComponent> {
    const serialNumber = isAsset(assetOrContractAsset)
      ? assetOrContractAsset.serialNumber
      : assetOrContractAsset.asset.serialNumber;

    return this.dialog.open(GenericModalComponent, {
      width: "450px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      restoreFocus: false,
      data: {
        title: `Create ticket for ${serialNumber}`,
        message: `To open a ticket on this Asset, please call or email: ${this.getInformationText(
          assetOrContractAsset.contract.maintenanceProvider,
        )}`,
        buttons: { hide: true },
      },
    });
  }

  createAttributeModal(
    assetService: AssetService,
    type?: DropdownOption,
  ): MatDialogRef<GenericModalComponent, Attribute> {
    //#region Questions
    const questions: QuestionBase[] = [
      new TextboxQuestion({
        value: type?.label,
        key: "type",
        label: "Attribute Type",
        required: true,
        disabled: true,
        order: 1,
      }),
      new TextboxQuestion({
        key: "name",
        label: "Attribute Name",
        required: true,
        order: 2,
      }),
    ];

    this.dialogRef = this.dialog.open<GenericModalComponent, GenericModalData>(GenericModalComponent, {
      width: MODAL.WIDTH,
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        title: "Create New",
        component: {
          clazz: DynamicFormComponent,
          onSubmit: "onSubmit",
          inputs: {
            questions,
            showButtons: false,
          },
          events: [
            {
              eventName: "formSubmit",
              handler: (_dynamicForm: DynamicFormComponent, values: { type: string; name: string }) => {
                this.addAttribute(values, assetService, _dynamicForm);
              },
            },
          ],
        },
      },
    });
    //#endregion
    return this.dialogRef;
  }

  /** Check if can edit an asset (on the table) */
  canEdit(currentUser: UserInfo, asset: Asset): boolean {
    return (
      (currentUser.isGlobal() && currentUser.hasRight(["admin", "manager"])) ||
      (!currentUser.isGlobal() &&
        currentUser.hasRight(["admin", "manager"]) &&
        !asset?.contract?.maintenanceProvider?.isChannelWorks)
    );
  }

  private getInformationText(maintenanceProvider: MaintenanceProvider): string {
    const info = [];
    if (maintenanceProvider.email) info.push(maintenanceProvider.email);
    if (maintenanceProvider.phone) info.push(maintenanceProvider.phone);
    return info.join(" - ");
  }

  private async getContract(contractService: ContractService, contractId: string): Promise<Contract> {
    return await contractService.getContract(contractId, { relations: { maintenanceProvider: true } }).toPromise();
  }

  private addAsset(asset: Asset, table: DataTableComponent<Asset>, assetService: AssetService): void {
    for (const propName in asset) {
      if (asset[propName] === "") {
        delete asset[propName];
      }
    }

    assetService.createAsset(asset).subscribe(
      () => {
        this.notificationService.success("Asset added");
        this.dialogRef.close();
        table.search();
      },
      (err) => {
        this.notificationService.error("Could not add Asset:\n " + err);
      },
    );
  }

  private addAssetEquipment(
    values: { oemName: string; modelName: string; typeName: string },
    existingIds: { oemExternalId?: number; modelExternalId?: number },
    assetService: AssetService,
    dynamicForm: DynamicFormComponent,
  ): void {
    const { oemName, modelName, typeName } = values;
    const { oemExternalId, modelExternalId } = existingIds;
    const request: AssetOemModelTypeRequest = { typeName };

    if (oemExternalId) {
      request["oemExternalId"] = oemExternalId;
    } else {
      request["oemName"] = oemName;
    }

    if (modelExternalId) {
      request["modelExternalId"] = modelExternalId;
    } else {
      request["modelName"] = modelName;
    }

    assetService.createAssetOemModelType(request).subscribe(
      (data) => {
        this.notificationService.success("Asset equipment created");
        this.dialogRef.close({
          oemExternalId: data.oemExternalId,
          oemName: data.oemName,
          modelExternalId: data.modelExternalId,
          modelName: data.modelName,
          typeExternalId: data.typeExternalId,
          typeName: data.typeName,
        });
      },
      (err) => {
        this.notificationService.error("Could not create asset equipment:\n " + err);
        dynamicForm.enable();
      },
    );
  }

  private addAttribute(
    values: { type: string; name: string },
    assetService: AssetService,
    dynamicForm: DynamicFormComponent,
  ): void {
    assetService.createAttribute(values).subscribe(
      (data) => {
        this.notificationService.success("Attribute created");
        this.dialogRef.close(data);
      },
      (err) => {
        this.notificationService.error("Could not create attribute:\n " + err);
        dynamicForm.enable();
      },
    );
  }
}
